/**
 * useAuthRedirect - OIDC callback i login flow nakon eID prijave.
 * Parsiranje tokena, /login poziv, automatske preference (uloga/org), navigacija.
 */

import { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import AuthService from '@/core/auth/AuthService';
import AccountService from '@/shared/services/AccountService';
import authDebug from '@/shared/utils/authDebug';
import { useAuth } from '@/shared/context/AuthContext';
import { dispatchInteractiveLoginComplete } from '@/shared/auth/authEvents';
import { applyPostLoginPreferences } from '../postLoginPreferences';

/**
 * OIDC callback i login flow nakon eID prijave.
 * Parsira token, poziva `/login`, primenjuje post-login preference i navigira.
 * @returns {{ isLoading: boolean, error: string|null, loadingStep: string }}
 */
export function useAuthRedirect() {
  const { setUser: setAuthUser } = useAuth();
  const navigate = useNavigate();
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingStep, setLoadingStep] = useState('Povezivanje sa eID serverom...');

  const hasProcessedRef = useRef(false);
  const abortControllerRef = useRef(null);
  const loginTimeoutRef = useRef(null);

  useEffect(() => {
    let isMounted = true;

    const processLogin = async () => {
      authDebug.loginStart();

      if (hasProcessedRef.current) {
        authDebug.warning('Callback već izvršen, preskačem');
        return;
      }

      const urlParams = new URLSearchParams(window.location.search);
      const hasCode = urlParams.has('code');
      const hasState = urlParams.has('state');

      if (!hasCode && !hasState) {
        authDebug.info('Nema URL parametara, proveravam postojećeg korisnika');
        try {
          const existingUser = await AuthService.getUser();
          if (existingUser && existingUser.access_token) {
            authDebug.success('Korisnik već postoji u storage-u');
            setAuthUser(existingUser);
            navigate('/', { state: { user: existingUser }, replace: true });
          }
        } catch (e) {
          authDebug.error('Greška pri proveri postojećeg korisnika', e);
        }
        return;
      }

      hasProcessedRef.current = true;
      if (isMounted) {
        setLoading(true);
        setLoadingStep('Povezivanje sa sistemom...');
      }
      authDebug.step('Obrada login callback-a započeta', { code: hasCode, state: hasState });

      try {
        authDebug.step('Parsiranje tokena iz URL callback-a');
        const tokenResult = await AuthService.getToken();

        if (!tokenResult) {
          authDebug.warning('Token rezultat je null (No matching state obrađen), preskačem');
          if (isMounted) setLoading(false);
          return;
        }
        authDebug.success('Token parsiran i sačuvan', { hasResult: !!tokenResult });

        await new Promise((resolve) => {
          requestAnimationFrame(() => setTimeout(resolve, 50));
        });
        if (isMounted) setLoadingStep('Учитавање података...');
        authDebug.step('Učitavanje korisničkih podataka');

        let userData;
        try {
          userData = await Promise.race([
            AuthService.getUser(),
            new Promise((_, reject) =>
              setTimeout(() => reject(new Error('Timeout: getUser() je predugo trajao')), 5000)
            ),
          ]);
          authDebug.success('Korisnik učitan', { hasUser: !!userData });
        } catch (getUserError) {
          authDebug.warning('Greška pri getUser(), pokušavam ponovo', getUserError);
          userData = await AuthService.getUser();
          authDebug.success('Korisnik učitan (retry)', { hasUser: !!userData });
        }

        if (!userData) {
          authDebug.warning('Korisnik nije učitan, čekam i pokušavam ponovo');
          await new Promise((resolve) => {
            requestAnimationFrame(() => setTimeout(resolve, 400));
          });
          userData = await AuthService.getUser();
          if (!userData) {
            authDebug.error('Korisnik nije učitan ni nakon retry-a');
            throw new Error('Кориснички подаци нису учитани. Молимо покушајте поново.');
          }
          authDebug.success('Korisnik uspešno prijavljen (retry)');
        } else {
          authDebug.success('Korisnik uspešno prijavljen');
        }

        // Ne postavljaj user ovde — Header bi odmah pokrenuo zaštićene API pozive (notification/search)
        // pre nego što BE /login potvrdi sesiju → 401 → renewal → iframe blocked → session clear.
        // setAuthUser se poziva tek posle uspešnog /login odgovora (sa loginData) na L169.

        const abortController = new AbortController();
        abortControllerRef.current = abortController;
        let loginTimeoutId = null;
        loginTimeoutRef.current = null;

        try {
          authDebug.step('Poziv backend login endpoint-a');
          const loginTimeout = 15000;
          const loginPromise = AccountService.checkLogin(abortController.signal);
          // Signal je namerno ignorisan u AccountService.checkLogin (deduplication).
          // Ako timeout pobedi race, loginPromise nastavlja da se izvršava i može da rejectuje
          // sa SessionExpiredError bez ijednog catch handlera → unhandled rejection → CRA overlay.
          loginPromise.catch(() => {});
          const timeoutPromise = new Promise((_, reject) => {
            loginTimeoutId = setTimeout(() => {
              authDebug.warning('Login timeout - otkazujem zahtev');
              abortController.abort();
              reject(new Error('Login захтев је предуго трајао. Молимо покушајте поново.'));
            }, loginTimeout);
            loginTimeoutRef.current = loginTimeoutId;
          });

          const loginResponse = await Promise.race([loginPromise, timeoutPromise]);

          if (loginTimeoutId) {
            clearTimeout(loginTimeoutId);
            loginTimeoutId = null;
            loginTimeoutRef.current = null;
          }
          abortControllerRef.current = null;

          if (!loginResponse || !loginResponse.profile) {
            authDebug.error('Login endpoint vratio prazan odgovor, odjavljujem korisnika');
            try {
              await AuthService.removeUser();
            } catch (removeError) {
              authDebug.warning('Greška pri uklanjanju korisnika', removeError);
            }
            setAuthUser(null);
            sessionStorage.setItem(
              'loginError',
              'Грешка при пријављивању. Нисте успешно улоговани. Молимо покушајте поново.'
            );
            navigate('/', { replace: true });
            return;
          }
          authDebug.success('Backend login endpoint uspešan', { hasData: !!loginResponse });

          if (loginResponse && userData) {
            userData.loginData = {
              profile: loginResponse.profile || {},
              roles: loginResponse.roles || [],
              organizations: loginResponse.organizations || { requested: [], verified: [] },
            };
            setAuthUser(userData);
            if (isMounted) {
              authDebug.info('User state ažuriran sa role podacima', {
                hasProfile: !!userData.loginData.profile,
                rolesCount: userData.loginData.roles.length,
                roles: userData.loginData.roles,
                hasOrganizations: !!userData.loginData.organizations,
              });
            }
          }
        } catch (loginError) {
          if (loginTimeoutId) {
            clearTimeout(loginTimeoutId);
            loginTimeoutId = null;
          }
          if (loginTimeoutRef.current) {
            clearTimeout(loginTimeoutRef.current);
            loginTimeoutRef.current = null;
          }
          abortControllerRef.current = null;

          // Ako je component već unmount-ovan (npr. korisnik navigirao pre završetka),
          // ne radi logout — setAuthUser i navigate bi radili na "mrtvom" mount-u.
          if (!isMounted) return;

          authDebug.error('Greška pri pozivu LOGIN endpoint-a, odjavljujem korisnika', loginError);

          const isCanceled =
            loginError.code === 'ECONNABORTED' ||
            loginError.message?.toLowerCase().includes('canceled') ||
            loginError.message?.toLowerCase().includes('cancelled') ||
            loginError.message?.toLowerCase().includes('aborted') ||
            loginError.name === 'CanceledError' ||
            loginError.name === 'AbortError' ||
            (loginError.config && loginError.config.signal?.aborted);
          const isTimeout =
            loginError.code === 'ECONNABORTED' ||
            loginError.message?.toLowerCase().includes('timeout') ||
            loginError.message?.toLowerCase().includes('predugo trajao');
          // SessionExpiredError tokom OIDC callback-a znači da je renewal pao usred svežeg login-a.
          // AccountService._doCheckLogin ga već mapira u "Prijava nije uspela", ali kao odbrana u dubinu
          // proveravamo i ovde po .name da ne propustimo originalnu "Sesija je istekla" poruku korisniku.
          const isSessionExpired = loginError?.name === 'SessionExpiredError';

          try {
            await AuthService.removeUser();
          } catch (removeError) {
            authDebug.warning('Greška pri uklanjanju korisnika', removeError);
          }
          setAuthUser(null);
          let errorMessage = 'Грешка при пријављивању. Молимо покушајте поново.';
          if (isCanceled || isTimeout) {
            errorMessage =
              'Захтев за пријављивање је прекинут или је предуго трајао. Молимо покушајте поново.';
          } else if (isSessionExpired) {
            errorMessage = 'Пријава није успела. Молимо покушајте поново.';
          } else if (loginError.message) {
            errorMessage = loginError.message;
          }
          sessionStorage.setItem('loginError', errorMessage);
          navigate('/', { replace: true });
          return;
        } finally {
          if (loginTimeoutId) {
            clearTimeout(loginTimeoutId);
            loginTimeoutId = null;
          }
          if (loginTimeoutRef.current) {
            clearTimeout(loginTimeoutRef.current);
            loginTimeoutRef.current = null;
          }
        }

        const { targetPath } = await applyPostLoginPreferences(userData);
        authDebug.info('Post-login preference i ruta', {
          targetPath,
          userRoles: userData?.loginData?.roles || [],
        });

        dispatchInteractiveLoginComplete();
        if (isMounted) setLoadingStep('Završavanje...');
        authDebug.step('Čekam da se React state ažurira');

        await new Promise((resolve) => {
          requestAnimationFrame(() => {
            requestAnimationFrame(() => setTimeout(resolve, 300));
          });
        });
        if (isMounted) setLoadingStep('Preusmeravanje...');
        authDebug.step('Navigacija nakon prijave', { targetPath });
        navigate(targetPath, { state: { user: userData }, replace: true });
        authDebug.loginComplete(true, { hasUser: !!userData });
      } catch (err) {
        if (!isMounted) return;
        authDebug.error('Greška tokom eID login callback-a', err);
        const errorMessage = err.message || err.toString() || 'Greška pri prijavljivanju';

        const isStateError =
          errorMessage.includes('No matching state') ||
          errorMessage.includes('state') ||
          errorMessage.toLowerCase().includes('state found in storage');

        if (isStateError) {
          const urlParams = new URLSearchParams(window.location.search);
          const hasCode = urlParams.has('code');
          if (hasCode) {
            authDebug.warning('Detektovana "No matching state" greška');
            authDebug.step('Čekam da vidim da li će drugi poziv uspeti');
            await new Promise((resolve) => {
              requestAnimationFrame(() => setTimeout(resolve, 1800));
            });
            try {
              const existingUser = await AuthService.getUser();
              if (existingUser && existingUser.access_token) {
                authDebug.success(
                  'Korisnik pronađen u session storage nakon čekanja - drugi poziv uspeo'
                );
                window.history.replaceState({}, document.title, window.location.pathname);
                authDebug.step('Pozivam setAuthUser sa postojećim korisnikom');
                setAuthUser(existingUser);
                await new Promise((resolve) => {
                  requestAnimationFrame(() => {
                    requestAnimationFrame(() => setTimeout(resolve, 300));
                  });
                });
                if (isMounted) {
                  authDebug.step('Navigiram na početnu stranicu');
                  navigate('/', { state: { user: existingUser }, replace: true });
                }
                return;
              } else {
                authDebug.warning(
                  'Korisnik NIJE pronađen ni nakon čekanja, čistim session storage'
                );
                await AuthService.removeUser().catch(() => {
                  authDebug.step('removeUser() ne radi, ručno čistim');
                  const keysToRemove = [];
                  for (let i = 0; i < sessionStorage.length; i++) {
                    const key = sessionStorage.key(i);
                    if (key && (key.includes('oidc') || key.includes('Oidc'))) {
                      keysToRemove.push(key);
                    }
                  }
                  keysToRemove.forEach((key) => sessionStorage.removeItem(key));
                });
                authDebug.success('Očistio OIDC podatke iz session storage-a');
                window.history.replaceState({}, document.title, window.location.pathname);
                setTimeout(() => {
                  if (isMounted) navigate('/');
                }, 500);
                return;
              }
            } catch (checkError) {
              authDebug.error('Greška pri proveri korisnika', checkError);
              window.history.replaceState({}, document.title, window.location.pathname);
              setTimeout(() => {
                if (isMounted) navigate('/');
              }, 500);
              return;
            }
          }
        }

        const isTemporaryError =
          errorMessage.includes('timeout') || errorMessage.includes('network');
        if (isTemporaryError) {
          setTimeout(async () => {
            if (!isMounted) return;
            try {
              const retryUser = await AuthService.getUser();
              if (retryUser) {
                setAuthUser(retryUser);
                setLoadingStep('Uspešno! Preusmeravanje...');
                setLoading(false);
                setTimeout(() => {
                  if (isMounted) navigate('/');
                }, 1000);
                return;
              }
            } catch (retryErr) {}
            setError(errorMessage);
            setLoading(false);
          }, 1000);
        } else {
          setError(errorMessage);
          setLoading(false);
        }
      }
    };

    processLogin();

    return () => {
      isMounted = false;
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
        abortControllerRef.current = null;
      }
      if (loginTimeoutRef.current) {
        clearTimeout(loginTimeoutRef.current);
        loginTimeoutRef.current = null;
      }
    };
  }, [navigate, setAuthUser]);

  return {
    isLoading: loading,
    error,
    loadingStep,
  };
}
